import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send, Trash2, Sparkles, Brain } from "lucide-react";
import { toast } from "sonner";
import { api, API, emitPostsChanged, pollAiPending } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import ReactMarkdown from "react-markdown";

const stripMemory = (t) => t.replace(/\[\[(MEMORY|AUTOFILL):[\s\S]*?\]\]/g, "").replace(/\[\[?[A-Z]*(:[\s\S]*)?$/, "").trim();

const SUGGESTIONS = [
  "Wir posten Dienstag ein Video, Mittwoch ein Foto und Donnerstag ein Video.",
  "Füll bitte Dienstag, Mittwoch, Donnerstag für die nächsten 2 Wochen aus.",
  "Plane mir die nächsten 2 Wochen Content.",
  "Welche Reel-Ideen passen diesen Monat?",
];

export default function AgentPanel({ open, onOpenChange }) {
  const { client, reload } = useClient();
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) api.get("/ai/chat", { params: { client_id: client?.id } }).then((r) => setMsgs(r.data));
  }, [open, client?.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, client_id: client?.id }),
      });
      if (!res.ok) throw new Error("Agent nicht erreichbar");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const data = p.replace(/^data: /, "");
          if (data === "[DONE]") continue;
          const ev = JSON.parse(data);
          if (ev.delta) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + ev.delta }; return c; });
          if (ev.memory) { toast.success("Agent hat sich das für " + (client?.name || "den Kunden") + " gemerkt"); reload(); }
          if (ev.autofill !== undefined) { if (ev.autofill) { toast.success(`${ev.autofill} Drive-Dateien eingeplant – Captions folgen gleich`); emitPostsChanged(); pollAiPending(client?.id); } else toast.info("Keine neuen Drive-Dateien zum Einplanen."); }
          if (ev.error) toast.error(ev.error);
        }
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => { await api.delete("/ai/chat", { params: { client_id: client?.id } }); setMsgs([]); };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col p-0 bg-[#33373f] dark text-foreground" data-testid="agent-panel">
        <SheetHeader className="px-6 py-5 border-b border-border">
          <SheetTitle className="flex items-center gap-2 text-foreground"><Sparkles className="h-4 w-4 text-purple-400" /> Content Agent
            <span className="ml-2 text-xs font-normal text-muted-foreground">{client ? `Kontext: ${client.name}` : ""}</span>
            <button onClick={clear} className="ml-auto mr-6 text-slate-500 hover:text-rose-400" data-testid="agent-clear-button" title="Verlauf löschen"><Trash2 className="h-4 w-4" /></button>
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto scroll-thin px-6 py-5 space-y-4" data-testid="agent-messages">
          {msgs.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground flex items-center gap-2"><Brain className="h-4 w-4" /> Ich kenne das heutige Datum, deinen Kalender und merke mir Regeln pro Kunde.</p>
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => send(s)} className="block w-full text-left text-sm px-4 py-3 rounded-lg border border-border hover:border-purple-500/50 hover:bg-purple-500/5 transition-colors" data-testid={`agent-suggestion-${i}`}>{s}</button>
              ))}
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`text-sm leading-relaxed rounded-xl px-4 py-3 ${m.role === "user" ? "bg-teal-500/15 ml-10 whitespace-pre-wrap" : "bg-white/[0.03] border border-border mr-4 agent-md"}`} data-testid={`agent-message-${m.role}`}>
              {stripMemory(m.content) ? (m.role === "user" ? m.content : <ReactMarkdown>{stripMemory(m.content)}</ReactMarkdown>) : <span className="text-muted-foreground animate-pulse">Denkt nach…</span>}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="p-4 border-t border-border flex gap-2">
          <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2} placeholder="Frag deinen Agenten…"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            className="resize-none" data-testid="ai-agent-chat-input" />
          <Button onClick={() => send()} disabled={busy} className="bg-purple-600 hover:bg-purple-500 h-auto" data-testid="ai-agent-send-button"><Send className="h-4 w-4" /></Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
